import type { ReactNode } from 'react';
import styled from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import type { WorkHoursInterval } from '../../common/business-hours-types';
import { useBusinessHoursProps } from '../business-hours-props-context';
import { useCurrentStatus } from '../current-status-context';
import { useScheduleContext } from './schedule-context';
import { ScheduleWorkHours } from './schedule-work-hours';

type ScheduleItemProps = {
  highlight: boolean;
  isClosed: boolean;
  title: ReactNode;
  caption?: ReactNode;
  hours: WorkHoursInterval[];
  largeOffset?: boolean;
  compactMode?: boolean;
};

export function ScheduleItem({
  highlight,
  isClosed,
  hours,
  title,
  caption,
  largeOffset,
  compactMode = false
}: ScheduleItemProps) {
  const { closedStatusColor, textColor, textStyle } = useBusinessHoursProps();
  const { isCurrentlyOpen } = useCurrentStatus();
  const { alignment } = useScheduleContext();
  const { t } = useLingui();

  const offset = largeOffset ? 16 : 8;

  const hoursLabel = isClosed ? t`Closed` : <ScheduleWorkHours hours={hours} />;
  return (
    <>
      <Title
        textStyle={{
          ...textStyle,
          fontWeight: highlight ? 'bold' : 'normal'
        }}
        color={highlight && !isCurrentlyOpen ? closedStatusColor : textColor}
        _align={alignment === 'center' ? 'right' : alignment}
        _offset={caption ? 4 : offset}
        _compactMode={compactMode}
      >
        {title}
      </Title>
      <Hours
        textStyle={{
          ...textStyle,
          fontWeight: highlight ? 'bold' : 'normal'
        }}
        color={highlight && !isCurrentlyOpen ? closedStatusColor : textColor}
        _offset={caption ? 4 : offset}
        _align={alignment === 'center' ? 'left' : alignment}
      >
        {hoursLabel}
      </Hours>
      {caption && (
        <Caption
          color={getColorWithAlpha(textColor, 0.7)}
          textStyle={{ ...textStyle, fontSize: textStyle.fontSize - 1 }}
        >
          {caption}
        </Caption>
      )}
    </>
  );
}

const Caption = styled(Typography)`
  margin-bottom: 16px;
  grid-column: 1 / span 2;
`;
const Hours = styled(Typography)<{
  _align: 'left' | 'center' | 'right';
  _offset: number;
}>`
  display: block;
  grid-column: 2;
  margin-bottom: ${({ _offset }) => _offset}px;
  text-align: ${({ _align }) => _align};
`;
const Title = styled(Typography)<{
  _align: 'left' | 'center' | 'right';
  _offset: number;
  _compactMode: boolean;
}>`
  display: block;
  grid-column: 1;
  max-width: 100%;
  margin-bottom: ${({ _offset }) => _offset}px;
  text-align: ${({ _align }) => _align};
  ${({ _compactMode }) => !_compactMode && 'width: max-content;'}
`;
